import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { selectActiveToolName, selectTextTemplate, setActiveTool } from "../tool-registry"
import { Marker } from "react-map-gl"
import { useEffect, useMemo, useState } from "react"
import { LngLatCoordinatePair } from "features/map/event/types"
import { PickingInfo } from "@deck.gl/core"
import { useTranslation } from "@nova/use-translation"
import { selectActiveTextId, publishLocalText, setLocalText } from "./slice"
import { TextToolInput } from "./TextToolInput"
import { selectActivatedFireId, useMapStylesJSON } from "features/map/control/selectors"
import {
    defaultTextToolContent,
    ToolName,
    LineWidth,
    minZoom,
    SidePanelLayerType,
    LayerIndex,
} from "@nova/constants"
import { convertHexStringToRGBArray } from "@nova/util-strings"
import { TextLayer } from "@deck.gl/layers"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { Text } from "@nova/protobuf"
import {
    decodeNumberToColor,
    decodeNumberToRGBA,
    encodeColorToNumber,
} from "@nova/serializer/src/client/util"
import {
    selectVisibleTexts,
    selectNextTextNumber,
    selectTextsMap,
    selectAllTexts,
    selectTextById,
} from "features/entity-caching/texts/selectors"
import { scaleUpFontSize } from "./module"
import { createText, deleteText, updateText } from "features/pubsub/entityActions/cubes/text"
import { defaultDarkTextColor, defaultLightTextColor } from "./constants"
import { selectUserID } from "user/profile/selectors"
import { ibmPlexSans } from "configuration/fonts"
import { selectCurrentGeometryToolType } from "plugins/shared/geometry/SharedGeometrySlice"
import { CreateTextArgs } from "@nova/serializer/src/client/validators"
import { selectHoveredTextElementId } from "features/map/layers/selectors"
import { getViewport, useViewport } from "features/map/GlobalViewState"
import { getGlobalTerrainData } from "features/map/GlobalTerrainManager"
import { duplicateText, zoomToText } from "features/map/inspector-panel/texts/module"
import { PluginRegistry, PluginSpec } from "plugins/plugin-registry"
import {
    getGenericInspectorPanelComponent,
    getGenericMockableInspectorPanel,
    getGenericMockedInspectorPanelComponent,
} from "features/map/inspector-panel/inspector-panels/GenericInspectorPanel"
import { getGenericMockedInspectorPanelBody } from "features/map/inspector-panel/inspector-panels/bodies/GenericInspectorPanelBody"
import {
    getGenericInspectorPanelFooter,
    getGenericDuplicatableFooterActions,
} from "features/map/inspector-panel/inspector-panels/footers/GenericInspectorPanelFooter"
import {
    getGenericInspectorPanelHeader,
    getGenericMockedInspectorPanelHeader,
} from "features/map/inspector-panel/inspector-panels/headers/GenericInspectorPanelHeader"
import { InspectorPanelTextLayer } from "features/map/inspector-panel/texts/InspectorPanelTextLayer"
import store from "integrations/redux/store"
import {
    selectAllEntitiesMap,
    selectAncestorEntitiesByEntityId,
} from "panels/content/side-panel/slice"
import { selectLockedLayersMap, selectSingleLayer } from "panels/content/side-panel/slice.new"
import { getAncestors } from "panels/content/side-panel/utils"
import * as Icons from "@nova/component-icons"

import { useResizableSidePanelWidth } from "panels/content/side-panel/SidePanel"
import { resolveEventLngLat } from "plugins/resolveLngLat"
import { useIsEditor } from "tools/hooks/useIsEditor"
import { InspectorPanelPlugin } from "features/map/inspector-panel/useInspectorPanel"

export const textOutlineWidthMap: Record<LineWidth, number> = {
    [LineWidth.none]: 0,
    [LineWidth.normal]: 3,
    [LineWidth.thick]: 6,
}

const sdfArgsByWidth = {
    [LineWidth.none]: {
        fontSize: 128,
        sdf: true,
        smoothing: 0.05,
        buffer: 4 + textOutlineWidthMap[LineWidth.none],
    },
    [LineWidth.normal]: {
        fontSize: 128,
        sdf: true,
        smoothing: 0.05,
        buffer: 4 + textOutlineWidthMap[LineWidth.normal],
    },
    [LineWidth.thick]: {
        fontSize: 128,
        sdf: true,
        smoothing: 0.05,
        buffer: 4 + textOutlineWidthMap[LineWidth.thick],
    },
}

export function registerTextPlugin(registry: PluginRegistry) {
    registry
        .register({
            name: "text",
            displayName: "Text",
            mockedDisplayName: "New Text",
            sidePanelLayerType: SidePanelLayerType.Text,
            PrimaryIcon: Icons.InsertText,
            updateAction: updateText,
            deleteAction: deleteText,
            confirmDelete: () => false,
            protobufMessage: Text,
            selectAllEntities: selectAllTexts,
            selectSingleEntity: selectTextById,
            zoomTo: zoomToText,
            duplicate: duplicateText,
            search: (input, entity) =>
                entity.text.toLowerCase().includes(input.toLowerCase()) ? entity.text : undefined,
        })
        .implement("side-panel-layers-plugin")
        .implement("inspector-panel-plugin", (spec) => ({
            InspectorPanelComponent: getGenericMockableInspectorPanel({
                NonMocked: getNonMockedTextInspectorPanel(spec),
                Mocked: getGenericMockedInspectorPanelComponent({
                    Header: getGenericMockedInspectorPanelHeader(spec),
                    Body: getGenericMockedInspectorPanelBody({
                        RenderComponent: InspectorPanelTextLayer,
                    }),
                }),
            }),
            useFooterActions: getGenericDuplicatableFooterActions(spec),
        }))
        .implement("map-layer-plugin", () => ({
            useLayer: () => {
                const dispatch = useAppDispatch()
                const { isEditor } = useWorkspacePermissions()
                const someGeometryToolIsInUse = !!useAppSelector(selectCurrentGeometryToolType)

                const texts = useAppSelector(selectVisibleTexts)
                const activeTextId = useAppSelector(selectActiveTextId)
                const lockedLayersById = useAppSelector(selectLockedLayersMap)

                // DeckGL is considers certain text attributes (i.e boldness, outline color and width) as static values
                // meaning that we cannot pass assesors to the layer and let it get the attributes from the data itself
                // Work-around involves grouping the layers by these static values and passing these groups as different layers
                const textGroupedByStaticAttributes = useMemo(() => {
                    const inactiveTexts = texts.filter((text) => text.id !== activeTextId)
                    const textsWithGuaranteedContent = inactiveTexts.map((text) => ({
                        ...text,
                        text: text.text || defaultTextToolContent,
                    }))
                    const textGroups = new Map<string, Text[]>()
                    textsWithGuaranteedContent.forEach((text) => {
                        // Note: if the width is 0, we don't care what the outline color is, set it to 0
                        // This minimizes computing a bunch of extra layers if we don't have to
                        const outlineWidth = (text.outlineWidth ?? LineWidth.none) as LineWidth
                        const outlineColor =
                            outlineWidth !== LineWidth.none && text.outlineColor != null
                                ? text.outlineColor
                                : 0

                        const key = `${text.bold ? 1 : 0}-${outlineColor}-${outlineWidth}`

                        if (textGroups.has(key)) {
                            textGroups.get(key)!.push(text)
                        } else {
                            textGroups.set(key, [text])
                        }
                    })

                    return textGroups
                }, [activeTextId, texts])

                const textLayerProps = {
                    getSize: (d: Text) => d.fontSize,
                    pickable: isEditor,
                    background: false,
                    getColor: (d: Text) =>
                        new Uint8Array(
                            d.color
                                ? convertHexStringToRGBArray(decodeNumberToColor(d.color))!
                                : [0, 0, 0]
                        ),
                    getTextAnchor: "start" as unknown as "start",
                    getAlignmentBaseline: "bottom" as unknown as "bottom",
                    getPosition: (d: Text) => [d.longitude, d.latitude] as [number, number],
                    getText: (d: Text) => d.text,
                    sizeUnits: "meters" as unknown as "meters",
                    onClick: (info: PickingInfo) => {
                        const item = info.object as Text
                        const state = store.getState()
                        const entityMap = selectAllEntitiesMap(state)
                        if (
                            !item ||
                            !isEditor ||
                            someGeometryToolIsInUse ||
                            lockedLayersById[item.id] ||
                            !!getAncestors(item.id, entityMap).find((ancestor) => ancestor.locked)
                        )
                            return
                        dispatch(selectSingleLayer({ id: item.id }))
                        dispatch(setLocalText(undefined))
                        return false
                    },
                    getAngle: (d: Text) => -1 * (d.rotation || 0),

                    billboard: false,
                    parameters: { depthTest: false },
                }

                const makeLayerProps = (key: string) => {
                    const [boldStr, outlineColorStr, outlineWidthStr] = key.split("-")
                    const bold = boldStr === "1"
                    const outlineColor = Number(outlineColorStr)
                    const outlineWidth = Number(outlineWidthStr) as LineWidth

                    return {
                        fontFamily: ibmPlexSans.style.fontFamily,
                        fontWeight: bold ? 700 : 400,
                        fontSettings: sdfArgsByWidth[outlineWidth],
                        opacity: 1,
                        id: `text-plugin${bold ? "-bold" : ""}-outline${outlineColor}-width${outlineWidth}`,
                        outlineColor: decodeNumberToRGBA(outlineColor),
                        outlineWidth: textOutlineWidthMap[outlineWidth],
                        ...textLayerProps,
                    }
                }

                const textLayers = Array.from(
                    textGroupedByStaticAttributes,
                    ([key, textGroup]) =>
                        new TextLayer({
                            data: textGroup,
                            extensions: [...getGlobalTerrainData().extensions],
                            ...makeLayerProps(key),
                        })
                )

                return textLayers
            },
            zIndex: LayerIndex.Text,
        }))
        .implement("toolbox-plugin", () => ({
            name: ToolName.Text,
            submitHandler: publishLocalText,
            shortcut: { letters: ["T"] },
        }))
        .implement("map-modal-plugin", () => ({
            Modal: () => {
                const activeToolName = useAppSelector(selectActiveToolName())
                const { t } = useTranslation()
                const dispatch = useAppDispatch()
                const projectId = useAppSelector(selectActivatedFireId) || ""
                const hoveredTextElementId = useAppSelector(selectHoveredTextElementId)

                const userId = useAppSelector(selectUserID)

                const textTemplate = useAppSelector(selectTextTemplate)
                const nextTextNumber = useAppSelector(selectNextTextNumber)
                const mapStylesJSON = useMapStylesJSON()

                const active = activeToolName === ToolName.Text
                const [coords, setCoords] = useState<LngLatCoordinatePair | undefined>()
                const sidePanelWidth = useResizableSidePanelWidth()
                useEffect(() => {
                    const listener = (event: MouseEvent) => {
                        if (
                            active &&
                            (event.target as HTMLElement)?.className === "maplibregl-canvas"
                        ) {
                            const { longitude, latitude } = resolveEventLngLat(event)
                            setCoords([longitude, latitude])
                        } else {
                            setCoords(undefined)
                        }
                    }
                    window.addEventListener("mousemove", listener)
                    return () => {
                        window.removeEventListener("mousemove", listener)
                    }
                }, [active, sidePanelWidth])

                useEffect(() => {
                    const handleClick = (event: MouseEvent) => {
                        if (
                            active &&
                            (event.target as HTMLElement)?.className === "maplibregl-canvas" &&
                            userId
                        ) {
                            if (hoveredTextElementId) {
                                dispatch(setActiveTool(undefined))
                                return
                            }
                            const viewport = getViewport()
                            const newCoords = resolveEventLngLat(event)
                            setCoords([
                                newCoords.longitude,
                                newCoords.latitude,
                            ] as LngLatCoordinatePair)

                            const fontSize = (() => {
                                const defaultFontSize = Math.max(scaleUpFontSize(24), 1)
                                if (!textTemplate.fontSize) return defaultFontSize

                                const { value: templateFontSize, zoomLevel: templateZoomLevel } =
                                    textTemplate.fontSize
                                const zoomLevel = viewport.zoom

                                // If the zoom level hasn't changed since the textTemplate's fontSize was updated, use the template's font size.
                                if (Math.abs(templateZoomLevel - (zoomLevel || minZoom)) < 0.1)
                                    return templateFontSize

                                return defaultFontSize
                            })()

                            const textColor =
                                textTemplate?.color ||
                                encodeColorToNumber(
                                    mapStylesJSON.id === "satellite"
                                        ? defaultLightTextColor
                                        : defaultDarkTextColor
                                )

                            const newText: Omit<
                                Pick<Text, CreateTextArgs[number]>,
                                "id" | "name" | "index"
                            > = {
                                longitude: newCoords.longitude,
                                latitude: newCoords.latitude,
                                color: textColor,
                                outlineColor:
                                    textTemplate?.outlineColor ||
                                    encodeColorToNumber(
                                        decodeNumberToColor(textColor) === defaultDarkTextColor
                                            ? defaultLightTextColor
                                            : defaultDarkTextColor
                                    ),
                                outlineWidth: textTemplate?.outlineWidth ?? LineWidth.none,
                                bold: textTemplate.isBold,
                                text: defaultTextToolContent,
                                fontSize: Math.floor(fontSize),
                                baseFontSize: Math.floor(fontSize),
                                rotation: viewport.bearing
                                    ? (() => {
                                          let rotation = Math.floor(viewport.bearing % 360)
                                          if (rotation < 0) rotation = 360 + rotation
                                          return rotation
                                      })()
                                    : 0,
                                createdBy: {
                                    userId,
                                },
                            }

                            // Publish localText then reset it, if it exists, prior to creating new text.
                            dispatch(publishLocalText())

                            dispatch(createText({ data: newText }))
                            dispatch(setActiveTool(undefined))
                        } else {
                            setCoords(undefined)
                        }
                    }
                    window.addEventListener("click", handleClick)
                    return () => {
                        window.removeEventListener("click", handleClick)
                    }
                }, [
                    active,
                    dispatch,
                    projectId,
                    textTemplate?.color,
                    textTemplate.isBold,
                    textTemplate.fontSize,
                    nextTextNumber,
                    userId,
                    hoveredTextElementId,
                    mapStylesJSON.id,
                    textTemplate?.outlineColor,
                    textTemplate?.outlineWidth,
                    sidePanelWidth,
                ]) // Add view.viewState as a dependency if it changes
                return (
                    <>
                        {active && coords ? (
                            <Marker
                                longitude={coords[0]}
                                latitude={coords[1]}
                                anchor={"top-left"}
                                offset={[12, 16]}
                                style={{ pointerEvents: "none" }}
                            >
                                <div className="rounded-lg bg-gray-900 px-2 py-1 pointer-events-none select-none text-gray-50">
                                    <small>{t("Click to place text")}</small>
                                </div>
                            </Marker>
                        ) : null}
                    </>
                )
            },
        }))
        .implement("map-modal-plugin", () => ({
            Modal: () => {
                const textMap = useAppSelector(selectTextsMap)
                const activeText = useAppSelector(selectActiveTextId)
                const { zoom } = useViewport()
                const ancestors = useAppSelector(selectAncestorEntitiesByEntityId(activeText || ""))
                return (
                    <>
                        {activeText &&
                            textMap[activeText]?.visible &&
                            !ancestors.find((ancestor) => !ancestor.visible) && (
                                <TextToolInput
                                    key={activeText}
                                    text={textMap[activeText]}
                                    zoomLevel={zoom || minZoom}
                                />
                            )}
                    </>
                )
            },
        }))
}

function getNonMockedTextInspectorPanel(spec: PluginSpec<Text>) {
    const GenericInspectorPanel = getGenericInspectorPanelComponent({
        spec,
        Header: getGenericInspectorPanelHeader(spec),
        Body: InspectorPanelTextLayer,
        Footer: getGenericInspectorPanelFooter(),
    })
    const InspectorPanel = ({ id, plugin }: { id: string; plugin: InspectorPanelPlugin }) => {
        const isEditor = useIsEditor()
        if (!isEditor) return null
        return <GenericInspectorPanel id={id} plugin={plugin} />
    }
    return InspectorPanel
}
